/**
 * **BarneoSortWidget** - Компонент сортировки
 * 
 * Компонент предоставляет интерфейс для сортировки результатов поиска
 * с предустановленными опциями и кастомными слотами.
 * 
 * @component
 * @example
 * ```vue
 * <template>
 *   <barneo-sort-widget 
 *     v-model="sortValue"
 *     @sort-changed="handleSort"
 *   />
 * </template>
 * 
 * <script setup>
 * import { ref } from 'vue'
 * 
 * const sortValue = ref('relevance')
 * 
 * const handleSort = (sort) => {
 *   console.log('Сортировка изменена:', sort)
 * }
 * </script>
 * ```
 * 
 * @see {@link BarneoSortWidgetProps} - Props компонента
 * @see {@link BarneoSortWidgetEmits} - Events компонента
 * @see {@link BarneoSortWidgetSlots} - Slots компонента
 * @see {@link SortWidgetConfig} - Конфигурация компонента
 * @see {@link SortOption} - Тип опции сортировки
 */

import type { BarneoSortWidgetProps, SortWidgetEmits } from './components'
import type { SortWidgetSlots } from './slots'
import type { SortWidgetConfig, SortOption } from './models'

/**
 * Props компонента BarneoSortWidget
 * 
 * @interface BarneoSortWidgetProps
 * @description Все доступные props для компонента сортировки
 * 
 * @property {string} [modelValue] - Текущая сортировка (v-model)
 * @property {SortOption[]} [options] - Кастомные опции сортировки
 * @property {boolean} [showLabels] - Показывать метки опций
 * @property {string} [defaultSort] - Сортировка по умолчанию
 * @property {boolean} [darkMode] - Принудительная темная тема
 * @property {boolean} [disabled] - Отключить компонент
 * @property {SortWidgetConfig} [config] - Локальная конфигурация
 * 
 * @example
 * ```vue
 * <barneo-sort-widget
 *   v-model="sortValue"
 *   :options="customSortOptions"
 *   :show-labels="true"
 *   :dark-mode="true"
 *   :disabled="false"
 * />
 * ```
 */
export type { BarneoSortWidgetProps }

/**
 * Events компонента BarneoSortWidget
 * 
 * @interface SortWidgetEmits
 * @description Все доступные события компонента сортировки
 * 
 * @property {(sortValue: string) => void} update:modelValue - Изменена сортировка (v-model)
 * @property {(sortValue: string) => void} sortChanged - Изменена сортировка
 * @property {(isLoading: boolean) => void} loading - Изменение состояния загрузки
 * @property {(error: string) => void} error - Произошла ошибка
 * 
 * @example
 * ```vue
 * <barneo-sort-widget
 *   @sort-changed="handleSort"
 *   @loading="handleLoading"
 *   @error="handleError"
 * />
 * ```
 */
export type { SortWidgetEmits }

/**
 * Slots компонента BarneoSortWidget
 * 
 * @interface SortWidgetSlots
 * @description Все доступные слоты для кастомизации компонента
 * 
 * @property {Object} sortOptions - Кастомный контейнер с опциями сортировки
 * @property {Object} sortOption - Кастомная опция сортировки
 * @property {Object} loading - Состояние загрузки
 * @property {Object} error - Состояние ошибки
 * 
 * @example
 * ```vue
 * <barneo-sort-widget>
 *   <template #sort-options="{ sortOptions, currentSort, isLoading, handleSortChange }">
 *     <div class="custom-sort-container">
 *       <div 
 *         v-for="option in sortOptions" 
 *         :key="option.value"
 *         class="custom-sort-option"
 *         :class="{ active: option.value === currentSort }"
 *         @click="handleSortChange(option.value)"
 *       >
 *         {{ option.label }}
 *       </div>
 *     </div>
 *   </template>
 *   
 *   <template #sort-option="{ option, isActive, isDisabled }">
 *     <div 
 *       class="custom-sort-option" 
 *       :class="{ active: isActive, disabled: isDisabled }"
 *     >
 *       <span>{{ option.label }}</span>
 *       <span v-if="isActive" class="loading-indicator">⏳</span>
 *     </div>
 *   </template>
 *   
 *   <template #loading="{ isLoading }">
 *     <div v-if="isLoading">⏳ Обновление...</div>
 *   </template>
 * </barneo-sort-widget>
 * ```
 */
export type { SortWidgetSlots }

/**
 * Конфигурация компонента сортировки
 * 
 * @interface SortWidgetConfig
 * @description Настройки компонента сортировки
 * 
 * @property {boolean} [darkMode] - Принудительная темная тема
 * @property {string} [defaultSort] - Сортировка по умолчанию
 * @property {SortOption[]} [options] - Опции сортировки
 * 
 * @example
 * ```typescript
 * const config: SortWidgetConfig = {
 *   darkMode: false,
 *   defaultSort: 'relevance',
 *   options: [
 *     { value: 'relevance', label: 'По релевантности' },
 *     { value: 'price', label: 'По цене' },
 *     { value: 'name', label: 'По названию' }
 *   ]
 * }
 * ```
 */
export type { SortWidgetConfig }

/**
 * Опция сортировки
 * 
 * @interface SortOption
 * @description Структура опции сортировки
 * 
 * @property {string} value - Значение сортировки
 * @property {string} label - Отображаемая метка
 * @property {string} [direction] - Направление сортировки (asc, desc)
 * 
 * @example
 * ```typescript
 * const sortOption: SortOption = {
 *   value: 'price',
 *   label: 'По цене',
 *   direction: 'asc'
 * }
 * ```
 */
export type { SortOption }

/**
 * Предустановленные опции сортировки
 * 
 * @constant DEFAULT_SORT_OPTIONS
 * @description Стандартные опции сортировки
 * 
 * @example
 * ```typescript
 * const defaultOptions: SortOption[] = [
 *   { value: 'relevance', label: 'По релевантности' },
 *   { value: 'price_asc', label: 'По цене (возрастание)' },
 *   { value: 'price_desc', label: 'По цене (убывание)' },
 *   { value: 'name_asc', label: 'По названию (А-Я)' },
 *   { value: 'name_desc', label: 'По названию (Я-А)' },
 *   { value: 'newest', label: 'Сначала новые' },
 *   { value: 'popular', label: 'По популярности' }
 * ]
 * ```
 */
export const DEFAULT_SORT_OPTIONS: SortOption[] = [
  { value: 'relevance', label: 'По релевантности' },
  { value: 'price_asc', label: 'По цене (возрастание)' },
  { value: 'price_desc', label: 'По цене (убывание)' },
  { value: 'name_asc', label: 'По названию (А-Я)' },
  { value: 'name_desc', label: 'По названию (Я-А)' },
  { value: 'newest', label: 'Сначала новые' },
  { value: 'popular', label: 'По популярности' }
]

/**
 * Связанные файлы и типы:
 * 
 * - {@link BarneoSortWidget} - Сам компонент
 * - {@link useSortWidget} - Composable для сортировки
 * - {@link SortState} - Состояние сортировки
 * - {@link SortRequest} - Запрос сортировки к API
 * - {@link SortResponse} - Ответ API сортировки
 * 
 * @see {@link https://github.com/denis-muhortov/barneo-search-widget-lib} - Документация библиотеки
 */
